import { memo } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";

// Layouts
import Button from "@/Layouts/components/Buttons/Button";
import ActionCard from "@Layouts/components/Cards/ActionCard/ActionCard";
import SimpleDoubleChecking from "@/Layouts/components/Buttons/SimpleDoubleChecking";

// MUI
import { Box, Collapse, Grid, Skeleton, Typography } from "@mui/material";
import { TransitionGroup } from "react-transition-group";

// Icons
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AddCircleRounded from "@mui/icons-material/AddCircleRounded";

const DELETE_COLOR = "var(--gray-blue-color)";

function orderListBy(list = [], orderBy = "category") {
	const highOrder = [];
	const tree = [];

	list?.forEach((element) => {
		if (element?.[orderBy] && !highOrder.includes(element[orderBy])) {
			highOrder.push(element[orderBy]);
		}
	});

	highOrder.forEach((order) => {
		tree.push({
			title: order,
			list: list?.filter((element) => element[orderBy] === order),
		});
	});

	return tree;
}

const ActionCardRender = (props) => {
	const {
		element = null,
		isContentIncluded = false,
		title = "",
		content = "",
		classes = "",
		isDeleting = false,
		isEditable = false,
		action = {},
		identifier = "",
		onDeleteAction = () => {},
	} = props;

	const cardContent = isContentIncluded ? (
		<Typography className={"regular-pt-sans-pro-narrow-label fs-13"} component={"div"}>
			{element?.[content] || ""}
		</Typography>
	) : null;

	return (
		<ActionCard
			title={
				<Typography
					className={clsx({
						"regular-pt-sans-pro-narrow-label bold !text-[11pt] !w-[185px]": true,
						"!mb-1": !cardContent,
					})}
					noWrap
				>
					{element?.[title] || ""}
				</Typography>
			}
			content={cardContent}
			contextLength={element?.[content]?.length || 0}
			classes={clsx({
				relative: true,
				"action-card-small-padding": true,
				"[&_.MuiCardHeader-action>.MuiStack-root>.MuiPaper-root>button]:!top-[-2px]": !cardContent,
				[classes]: Boolean(classes),
			})}
			loading={action?.[identifier] === element?.[identifier] && isDeleting}
			action={
				<Box sx={{ position: "relative !important", right: 10, top: 0 }}>
					<SimpleDoubleChecking
						className={"btn-delete"}
						color={DELETE_COLOR}
						confirmationButtonColor={DELETE_COLOR}
						icon={<CloseRoundedIcon fontSize="small" />}
						iconHover={<CloseRoundedIcon fontSize="small" />}
						confirmationIcon={<DeleteOutlineOutlinedIcon fontSize="small" />}
						disabled={!isEditable}
						onDeleteAction={() => onDeleteAction?.(element)}
					/>
				</Box>
			}
		/>
	);
};
ActionCardRender.propTypes = {
	element: PropTypes.object,
	action: PropTypes.object,
	identifier: PropTypes.string,
	title: PropTypes.string,
	content: PropTypes.string,
	classes: PropTypes.string,
	isContentIncluded: PropTypes.bool,
	isDeleting: PropTypes.bool,
	isEditable: PropTypes.bool,
	onDeleteAction: PropTypes.func,
};

const AccessContent = (props) => {
	const {
		index = 0,
		data = [],
		rootIdentifier = "",
		cardTitle = "",
		cardContent = "",
		cardClasses = "",
		deleteDefinition = null,
		isContentIncluded = false,
		isDeleting = false,
		isEditable = false,
		isSingleLevel = false,
		onDeleteAction = () => {},
	} = props;

	const { identifier, action } = deleteDefinition || {};

	if (isSingleLevel) {
		return (
			<TransitionGroup component={Grid} container item spacing={2}>
				{data?.map((element, key) => {
					const itemKey = `item_${element.title}_${index}_${key}`;
					return (
						<Collapse key={itemKey} component={Grid} item xs={3}>
							<ActionCardRender
								element={element}
								isContentIncluded={isContentIncluded}
								title={cardTitle}
								content={cardContent}
								classes={cardClasses}
								isDeleting={isDeleting}
								isEditable={isEditable}
								action={action}
								identifier={identifier}
								onDeleteAction={onDeleteAction}
							/>
						</Collapse>
					);
				})}
			</TransitionGroup>
		);
	}

	return (
		<>
			{orderListBy(data, rootIdentifier).map((item) => {
				if (!item.list?.length) {
					return null;
				}

				return (
					<Grid key={item.title} container item mt={1}>
						<Grid item>
							<Typography className={"regular-montserrat-label fs-regular !text-neutral-gray"} mb={1}>
								{item.title}
							</Typography>
						</Grid>

						<TransitionGroup component={Grid} container item spacing={2}>
							{item.list?.map((element, key) => {
								const itemKey = `item_${item.title}_${index}_${key}`;
								return (
									<Collapse key={itemKey} component={Grid} item xs={3}>
										<ActionCardRender
											element={element}
											isContentIncluded={isContentIncluded}
											title={cardTitle}
											content={cardContent}
											classes={cardClasses}
											isDeleting={isDeleting}
											isEditable={isEditable}
											action={action}
											identifier={identifier}
											onDeleteAction={onDeleteAction}
										/>
									</Collapse>
								);
							})}
						</TransitionGroup>
					</Grid>
				);
			})}
		</>
	);
};
AccessContent.propTypes = {
	index: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	data: PropTypes.array,
	isSingleLevel: PropTypes.bool,
	rootIdentifier: PropTypes.string,
	cardTitle: PropTypes.string,
	cardContent: PropTypes.string,
	cardClasses: PropTypes.string,
	deleteDefinition: PropTypes.shape({
		identifier: PropTypes.string.isRequired,
		action: PropTypes.object,
	}),
	isContentIncluded: PropTypes.bool,
	isDeleting: PropTypes.bool,
	isEditable: PropTypes.bool,
	onDeleteAction: PropTypes.func,
};

function AccessCards(props) {
	const {
		index = 0,
		title = "",
		titleColor = "text-primary",
		buttonTitle = "Add",
		addAmount = 1,
		containerProps = {},
		cardClasses = "",
		cardProperties = {},
		loading = false,
		isAdding = false,
		isEditable = false,
		onOpenAction = () => {},
	} = props;

	return (
		<Grid container {...containerProps}>
			{loading ? (
				<Skeleton width={120} height={28} />
			) : (
				<Typography className={`regular-montserrat-label fs-18 !${titleColor}`}>{title}</Typography>
			)}

			<Grid container direction={"column"} mt={1}>
				{loading ? (
					<Grid container spacing={2} mt={1}>
						<Grid item xs={12}>
							<Skeleton width={150} height={20}></Skeleton>
						</Grid>
						<Grid container item spacing={2}>
							{Array.from(Array(4)).map((_, key) => {
								const itemKey = `loading_${title}_${index}_${key}`;
								return (
									<Grid key={itemKey} item xs={3}>
										<ActionCard
											{...cardProperties}
											classes={clsx({
												[cardClasses]: Boolean(cardClasses),
											})}
											title
											action
											loading={true}
										/>
									</Grid>
								);
							})}
						</Grid>
					</Grid>
				) : (
					<Grid container spacing={2}>
						<Grid key={`new_${title}_${index}`} container item spacing={2}>
							<AccessContent {...props} />
							{Array.from(Array(addAmount)).map((_, key) => {
								const itemKey = `adding_${title}_${index}_${key}`;
								if (isAdding) {
									return (
										<Grid key={itemKey} item xs={3}>
											<ActionCard
												{...cardProperties}
												classes={clsx({
													[cardClasses]: Boolean(cardClasses),
												})}
												title
												action
												loading={true}
											/>
										</Grid>
									);
								}
								return null;
							})}
						</Grid>
					</Grid>
				)}
			</Grid>

			<Grid container justifyContent={"center"} mt={3}>
				{loading ? (
					<Skeleton variant="rectangular" width={132} height={30} sx={{ borderRadius: "6px" }} />
				) : (
					<Button
						variant="contained"
						text={buttonTitle}
						classes="!bg-primary hover:!shadow-primary-shadow-50 h-[30px] !rounded-[6px] btn-regular-text"
						disabled={!isEditable}
						endIcon={<AddCircleRounded />}
						onClick={onOpenAction}
					/>
				)}
			</Grid>
		</Grid>
	);
}
AccessCards.propTypes = {
	index: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	actionOnIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	data: PropTypes.array,
	rootIdentifier: PropTypes.string,
	cardTitle: PropTypes.string,
	cardContent: PropTypes.string,
	title: PropTypes.string,
	titleColor: PropTypes.string,
	buttonTitle: PropTypes.string,
	addAmount: PropTypes.number,
	containerProps: PropTypes.object,
	cardProperties: PropTypes.object,
	cardClasses: PropTypes.string,
	loading: PropTypes.bool,
	isAdding: PropTypes.bool,
	isContentIncluded: PropTypes.bool,
	isDeleting: PropTypes.bool,
	isEditable: PropTypes.bool,
	isSingleLevel: PropTypes.bool,
	deleteDefinition: PropTypes.shape({
		identifier: PropTypes.string.isRequired,
		action: PropTypes.object,
	}),
	onOpenAction: PropTypes.func,
	onDeleteAction: PropTypes.func,
};

const compareFn = (prevProps, nextProps) => {
	const hasSameData = prevProps?.data?.length === nextProps?.data?.length;
	const hasSameLocation = prevProps?.locationId === nextProps?.locationId;
	const hasSameDeleting = prevProps?.isDeleting === nextProps?.isDeleting;
	const hasSameAdding = prevProps?.isAdding === nextProps?.isAdding;
	const hasSameActions = hasSameDeleting && hasSameAdding;
	const hasSameLoading = prevProps?.loading === nextProps?.loading;
	return hasSameData && hasSameActions && hasSameLocation && hasSameLoading; // FALSE to RENDER AGAIN, TRUE to NOT RENDER AGAIN
};

const AccessCardsContent = memo(AccessCards, compareFn);
export default AccessCardsContent;
